import { Star, MapPin, Shield, Users, Clock, Home, Waves, UtensilsCrossed, Car, CheckCircle, ChevronLeft, ChevronRight, Navigation } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { useRef, useState } from "react";
import { gtagReportConversion } from "@/lib/gtag";
import heroImage from "@/assets/hero-aerial.jpg";
import heroMobileImage from "@/assets/hero-mobile.jpg";
import familyTubing from "@/assets/family-tubing.jpg";
import cabanaLifestyle from "@/assets/cabana-lifestyle.jpg";
import glampingTent from "@/assets/glamping-tent.jpg";
import { BookingPopupModal } from "./BookingPopupModal";
import { GlampingBookingPopupModal } from "./GlampingBookingPopupModal";
import { TubingBookingPopupModal } from "./TubingBookingPopupModal";

// Gallery Images
import galleryRiverCanopies from "@/assets/gallery/river-canopies.jpg";
import galleryFriendsTubing from "@/assets/gallery/friends-tubing.jpg";
import gallerySonsSign from "@/assets/gallery/sons-sign.jpg";
import galleryRiverHangout from "@/assets/gallery/river-hangout.jpg";
import galleryClearRiver from "@/assets/gallery/clear-river.jpg";
import galleryTikiLounge from "@/assets/gallery/tiki-lounge.jpg";
import galleryFishingBobber from "@/assets/gallery/fishing-bobber.jpg";
import galleryCampfireSmores from "@/assets/gallery/campfire-smores.jpg";
import galleryAerialCabanas from "@/assets/gallery/aerial-cabanas.jpg";
import galleryRiverView from "@/assets/gallery/river-view.jpg";
import galleryGlampingLights from "@/assets/gallery/glamping-lights.jpg";
import galleryGrillingFood from "@/assets/gallery/grilling-food.jpg";
import galleryCabanaRiverScene from "@/assets/gallery/cabana-river-scene.jpg";
import galleryTreeCanopyPath from "@/assets/gallery/tree-canopy-path.jpg";
import galleryAerialRiverCabanas from "@/assets/gallery/aerial-river-cabanas.jpg";
import gallerySonsTubesStack from "@/assets/gallery/sons-tubes-stack.jpg";
import galleryTubingSelfie from "@/assets/gallery/tubing-selfie.jpg";
import galleryKidWhaleFloat from "@/assets/gallery/kid-whale-float.jpg";
import galleryKayakingFriends from "@/assets/gallery/kayaking-friends.jpg";
import galleryTubingCanyonView from "@/assets/gallery/tubing-canyon-view.jpg";
import galleryRiverTurtle from "@/assets/gallery/river-turtle.jpg";
import galleryGlampingTentExterior from "@/assets/gallery/glamping-tent-exterior.jpg";
import galleryKidsPlayingRiver from "@/assets/gallery/kids-playing-river.jpg";
import gallerySonsSignBanner from "@/assets/gallery/sons-sign-banner.jpg";
import coupleFloatingRiver from "@/assets/tubing/couple-floating-river.jpg";

// Hero Section
export function HeroSection() {
  const isMobile = useIsMobile();
  const [bookingOpen, setBookingOpen] = useState(false);
  const [glampingOpen, setGlampingOpen] = useState(false);
  const [tubingOpen, setTubingOpen] = useState(false);
  
  const features = [
    { icon: Star, text: "4.1★ Google Rating" },
    { icon: Users, text: "Family-Owned" },
    { icon: Clock, text: "10+ Years" },
    { icon: Shield, text: "Only 12 Cabanas" },
    { icon: Waves, text: "Private River Access" },
    { icon: CheckCircle, text: "No Hidden Fees" },
  ];

  return (
    <section className="relative min-h-screen flex flex-col overflow-hidden">
      {/* Background Video/Image */}
      <div className="absolute inset-0">
        {isMobile ? (
          <img fetchPriority="high" loading="eager" decoding="async"
            src={heroMobileImage}
            alt="Kids playing in the Guadalupe River at Son's riverside cabana"
            className="w-full h-full object-cover"
            width={1080}
            height={1920}
            
          />
        ) : (
          <div className="relative w-full h-full overflow-hidden">
            <iframe
              src="https://www.youtube-nocookie.com/embed/dXZnZHI68KU?autoplay=1&mute=1&loop=1&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&playlist=dXZnZHI68KU"
              title="Son's Guadalupe River Experience"
              className="absolute inset-0 w-full h-full pointer-events-none"
              allow="autoplay; encrypted-media"
              allowFullScreen
            />
            {/* Fallback image while video loads */}
            <img loading="eager" decoding="async"
              src={heroImage}
              alt="Aerial view of Son's Guadalupe private river cabanas"
              className="absolute inset-0 w-full h-full object-cover -z-10"
              width={1920}
              height={1080}
              fetchPriority="high"
            />
          </div>
        )}
        
      </div>

      {/* Content */}
      <div className="relative z-10 flex-1 flex items-center justify-center container-narrow mx-auto px-4 md:px-8 text-center pt-24 pb-32">
        <div className="max-w-4xl mx-auto">
          {/* Headline */}
          <h1 className="font-lora text-4xl md:text-5xl lg:text-6xl font-bold text-teal mb-4 opacity-0 animate-fade-up text-balance [text-shadow:_0_3px_12px_rgba(0,0,0,0.7)] md:[text-shadow:none]">
            The Guadalupe, Done Right
          </h1>
          <p className="text-xl md:text-2xl text-white mb-10 opacity-0 animate-fade-up stagger-1 font-medium [text-shadow:_0_2px_10px_rgba(0,0,0,0.6)] md:[text-shadow:none]">
            Float, Relax, Grill, Enjoy!
          </p>

          {/* CTAs */}
          <div className="flex flex-col gap-4 justify-center items-center opacity-0 animate-fade-up stagger-1">
            <Button variant="hero" size="xl" onClick={() => { gtagReportConversion(); setBookingOpen(true); }}>
              Reserve Your Private Cabana
            </Button>
            <Button variant="hero-outline" size="xl" onClick={() => { gtagReportConversion(); setTubingOpen(true); }}>
              Go Tubing
            </Button>
          </div>
        </div>
      </div>
      
      <BookingPopupModal open={bookingOpen} onOpenChange={setBookingOpen} />
      <GlampingBookingPopupModal open={glampingOpen} onOpenChange={setGlampingOpen} />
      <TubingBookingPopupModal open={tubingOpen} onOpenChange={setTubingOpen} />

      {/* Scroll Indicator */}
      <div className="absolute bottom-28 left-1/2 -translate-x-1/2 animate-float z-10">
        <div className="w-6 h-10 rounded-full border-2 border-white/50 flex items-start justify-center p-2">
          <div className="w-1 h-3 rounded-full bg-white/50 animate-pulse" />
        </div>
      </div>

      {/* Bottom Feature Bar */}
      <div className="relative z-10 bg-teal w-full">
        <div className="container-narrow mx-auto px-4 md:px-8 py-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
            {features.map((feature, index) => (
              <div key={index} className="flex items-center gap-3">
                <feature.icon className="w-5 h-5 text-white/80 flex-shrink-0" />
                <span className="text-white text-sm font-medium">{feature.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// Photo Gallery Section
export function PhotoGallerySection() {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  const galleryImages = [
    { src: galleryRiverView, alt: "Beautiful view of the Guadalupe River with tubers" },
    { src: galleryAerialCabanas, alt: "Aerial view of thatched-roof cabanas" },
    { src: galleryTubingSelfie, alt: "Friends taking a selfie while tubing" },
    { src: galleryRiverCanopies, alt: "Colorful canopies over picnic tables in the river" },
    { src: galleryGlampingLights, alt: "Glamping tent with string lights at dusk" },
    { src: galleryKidWhaleFloat, alt: "Kid floating on a whale float in the river" },
    { src: galleryTikiLounge, alt: "Tiki lounge overlooking the river" },
    { src: galleryGrillingFood, alt: "Grilling sausages and food at the cabana" },
    { src: galleryKayakingFriends, alt: "Friends kayaking down the river" },
    { src: galleryRiverHangout, alt: "Families enjoying the river at the cabana" },
    { src: galleryCampfireSmores, alt: "Making s'mores at the fire pit by the river" },
    { src: galleryTubingCanyonView, alt: "Tubing with scenic canyon views" },
    { src: galleryAerialRiverCabanas, alt: "Aerial view of cabanas along the river" },
    { src: galleryClearRiver, alt: "Crystal clear Guadalupe River waters" },
    { src: galleryRiverTurtle, alt: "Holding a river turtle on the water" },
    { src: gallerySonsTubesStack, alt: "Stack of Son's Tubes by the river" },
    { src: galleryGlampingTentExterior, alt: "Glamping tent exterior near cabanas" },
    { src: galleryFriendsTubing, alt: "Friends ready for tubing adventure" },
    { src: galleryKidsPlayingRiver, alt: "Kids playing in the shallow river" },
    { src: galleryTreeCanopyPath, alt: "Tree-lined path with American flag" },
    { src: galleryFishingBobber, alt: "Fishing bobber on the line" },
    { src: gallerySonsSign, alt: "Son's Guadalupe welcome sign" },
  ];

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -340, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 340, behavior: 'smooth' });
    }
  };

  return (
    <section className="py-12 md:py-16 bg-background">
      <div className="container-narrow mx-auto px-4 md:px-8 mb-8">
        <h2 className="font-lora text-3xl md:text-4xl font-bold text-center text-teal">
          Enjoy an Amazing Time in and on the Banks of the Guadalupe River!
        </h2>
      </div>
      
      {/* Scrollable Gallery with Navigation */}
      <div className="relative">
        {/* Left Arrow - Desktop Only */}
        <button
          onClick={scrollLeft}
          className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 z-10 w-12 h-12 items-center justify-center rounded-full bg-white/90 shadow-lg hover:bg-white transition-colors"
          aria-label="Scroll left"
        >
          <ChevronLeft className="w-6 h-6 text-charcoal" />
        </button>

        {/* Right Arrow - Desktop Only */}
        <button
          onClick={scrollRight}
          className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 z-10 w-12 h-12 items-center justify-center rounded-full bg-white/90 shadow-lg hover:bg-white transition-colors"
          aria-label="Scroll right"
        >
          <ChevronRight className="w-6 h-6 text-charcoal" />
        </button>

        <div 
          ref={scrollContainerRef}
          className="flex gap-4 overflow-x-auto pb-4 px-4 md:px-8 snap-x snap-mandatory scrollbar-hide"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {galleryImages.map((image, index) => (
            <div 
              key={index}
              className="flex-shrink-0 snap-center first:ml-auto last:mr-auto"
            >
              <div className="w-72 md:w-80 h-56 md:h-64 rounded-xl overflow-hidden shadow-card">
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Problem Section
export function ProblemSection() {
  const nightmares = [
    "Shoulder-to-shoulder crowds fighting for the same exit point",
    "Hunting for parking while worried about getting towed",
    "Rowdy atmosphere that makes you nervous for the kids",
    "Ending the float far from your car with exhausted children",
    "Nowhere to sit, rest, or escape the sun",
    "Porta-potties as your only bathroom option",
  ];

  return (
    <section className="section-padding bg-muted">
      <div className="container-narrow mx-auto">
        <h2 className="font-heading text-3xl md:text-4xl font-bold text-center text-foreground mb-4">
          If You've Ever Had a River Trip Go Wrong...
        </h2>
        <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
          You know the feeling. The day started with excitement and ended with regret.
        </p>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Nightmares List */}
          <div className="bg-card rounded-2xl p-8 shadow-card">
            <h3 className="font-heading text-xl font-bold text-foreground mb-6 flex items-center gap-2">
              <span className="text-2xl">😓</span> Sound Familiar?
            </h3>
            <ul className="space-y-4">
              {nightmares.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="text-destructive text-lg">✕</span>
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Solution Teaser */}
          <div className="bg-deep-green rounded-2xl p-8 text-primary-foreground">
            <h3 className="font-heading text-xl font-bold mb-4">
              You're Not Alone
            </h3>
            <p className="text-primary-foreground/90 leading-relaxed mb-6">
              The Guadalupe River itself is gorgeous. Crystal-clear water, cypress trees, limestone canyons — 
              it's one of the most beautiful places in Texas.
            </p>
            <p className="text-primary-foreground/90 leading-relaxed mb-6">
              <strong className="text-warm-sand">The problem isn't the river. It's the lack of control.</strong>
            </p>
            <p className="text-primary-foreground/90 leading-relaxed">
              That's why we built Son's Guadalupe differently — as a private basecamp system designed 
              to eliminate every moment when river trips fall apart.
            </p>
            <a href="#how-the-day-flows">
              <Button variant="warm" className="mt-6">
                See How We're Different
              </Button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

// Full Width Image Break Section
export function ImageBreakSection() {
  return (
    <section className="relative h-64 md:h-96 overflow-hidden">
      <img
        src={galleryAerialRiverCabanas}
        alt="Aerial view of Son's Guadalupe cabanas along the beautiful river"
        className="w-full h-full object-cover"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-charcoal/30 to-transparent" />
    </section>
  );
}

// How the Day Flows Section
export function HowTheDayFlowsSection() {
  const steps = [
    {
      number: "1",
      title: "Settle Into Your Space",
      description: "Arrive, unload, and relax at your private cabana or riverside picnic table. Shade, seating, and a place to call home for the day—no rushing, no setup stress.",
    },
    {
      number: "2",
      title: "Float When You're Ready",
      description: "When the timing feels right, hop on our shuttle upstream and enjoy an easy 2–3 hour float down the Guadalupe. No crowds to fight, no logistics to manage—just a smooth, scenic ride.",
    },
    {
      number: "3",
      title: "Come Back to Comfort",
      description: "Your float ends right where it began. Return to your shaded space, fire up the grill, cool off, and enjoy the rest of the day at your own pace.",
    },
    {
      number: "4",
      title: "Stay the Night (If You Want To)",
      description: "Turn your river day into an overnight escape in a riverside bell tent with AC. Wind down by the fire pits, watch the stars come out, and fall asleep to the sound of the Guadalupe just steps away.",
    },
  ];

  return (
    <section id="how-the-day-flows" className="section-padding bg-muted">
      <div className="container-narrow mx-auto">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
            How the Day Flows
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, index) => (
            <div
              key={index}
              className="bg-card rounded-2xl p-6 shadow-soft hover:shadow-card transition-shadow duration-300"
            >
              <div className="w-12 h-12 rounded-full bg-teal flex items-center justify-center mb-4">
                <span className="text-white font-bold text-xl">{step.number}</span>
              </div>
              <h3 className="font-heading text-lg font-bold text-foreground mb-3">
                {step.title}
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {step.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Differentiators Section
export function DifferentiatorsSection() {
  const differentiators = [
    {
      icon: Shield,
      image: galleryTikiLounge,
      title: "Private Riverside Cabana Rentals",
      description: "Reserved space with thatched-palm shade, BBQ pit, seating, picnic table, water & electricity. Your living room on the river.",
    },
    {
      icon: UtensilsCrossed,
      image: galleryRiverCanopies,
      title: "Rent In-Water Picnic Tables",
      description: "Enjoy a unique dining experience with picnic tables right in the river. Dip your feet while you eat!",
    },
    {
      icon: Home,
      image: galleryTubingCanyonView,
      title: "Float Back to Your Doorstep",
      description: "Most trips end at crowded parking lots. Ours ends right at your private cabana. Your stuff is waiting, the grill is ready.",
    },
    {
      icon: Users,
      image: galleryAerialCabanas,
      title: "Limited Capacity",
      description: "A Private Stretch of the Guadalupe—Shared by Very Few. Thoughtfully limited so every group has room to relax.",
    },
    {
      icon: Star,
      image: galleryKidsPlayingRiver,
      title: "Family-Friendly Environment",
      description: "Quiet hours enforced, on-site staff, curated atmosphere. No rowdy strangers ruining your day.",
    },
    {
      icon: MapPin,
      image: gallerySonsSignBanner,
      title: "Everything in One Place",
      description: "Parking, tubes, shuttle, cabana, glamping — all here. Park once, enjoy all day. No logistics nightmares.",
    },
  ];

  return (
    <section className="section-padding bg-background">
      <div className="container-narrow mx-auto">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
            What Makes a Son's Guadalupe Experience Unique and Special?
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {differentiators.map((item, index) => (
            <div
              key={index}
              className={`bg-card rounded-2xl overflow-hidden shadow-soft hover:shadow-card transition-shadow duration-300 ${
                index === 4 ? "md:col-span-2 lg:col-span-1" : ""
              }`}
            >
              <div className="h-48 md:h-56 overflow-hidden">
                <img loading="lazy" decoding="async" 
                  src={item.image} 
                  alt={item.title}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <div className="w-10 h-10 rounded-lg bg-coral-orange/20 flex items-center justify-center mb-3">
                  <item.icon className="w-5 h-5 text-coral-orange" />
                </div>
                <h3 className="font-heading text-lg font-bold text-foreground mb-2">
                  {item.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Testimonials Section
export function TestimonialsSection() {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  const testimonials = [
    {
      quote: "I just want a stress-free day where nobody fights and we all go home happy. Son's Guadalupe delivered exactly that.",
      author: "Sarah M.",
      location: "San Antonio, TX",
      rating: 5,
    },
    {
      quote: "Pleasantly surprised with how nice the showers were, and the cabana felt like our own private oasis. Already planning our next trip!",
      author: "Michael R.",
      location: "Austin, TX",
      rating: 5,
    },
    {
      quote: "Very family-friendly location compared to others on River Road. Our kids felt safe and we could actually relax.",
      author: "Jennifer & Tom H.",
      location: "Houston, TX",
      rating: 5,
    },
    {
      quote: "KAT IS THE BEST! She checked in on us numerous times to make sure we had everything we needed. Above and beyond service.",
      author: "David L.",
      location: "Dallas, TX",
      rating: 5,
    },
    {
      quote: "The tubing experience was incredible! Crystal clear water and ending right back at our cabana was a game changer. No more long walks back to the car!",
      author: "Amanda K.",
      location: "Fort Worth, TX",
      rating: 5,
    },
    {
      quote: "We've tried other spots on the Guadalupe and nothing compares. The private cabana made all the difference for our family reunion.",
      author: "Robert & Lisa T.",
      location: "Corpus Christi, TX",
      rating: 5,
    },
    {
      quote: "Glamping was such a unique experience! Fell asleep to the sound of the river and woke up refreshed. Can't wait to come back!",
      author: "Christina P.",
      location: "Austin, TX",
      rating: 5,
    },
    {
      quote: "Perfect for a bachelorette weekend! The staff was so accommodating and the views were absolutely stunning.",
      author: "Megan W.",
      location: "San Marcos, TX",
      rating: 5,
    },
    {
      quote: "My kids are already asking when we can go back. The shallow areas were perfect for the little ones to play safely.",
      author: "Jason & Emily R.",
      location: "New Braunfels, TX",
      rating: 5,
    },
    {
      quote: "First time tubing and I loved every minute. The team made sure we had everything we needed. Highly recommend!",
      author: "Carlos M.",
      location: "Laredo, TX",
      rating: 5,
    },
  ];

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -380, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 380, behavior: 'smooth' });
    }
  };

  return (
    <section className="section-padding bg-secondary/10">
      <div className="container-narrow mx-auto">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
            What Families Are Saying
          </h2>
          <p className="text-muted-foreground">
            Real reviews from real families who found their calm on the Guadalupe
          </p>
        </div>

        {/* Scrollable Testimonials with Navigation */}
        <div className="relative">
          {/* Left Arrow - Desktop Only */}
          <button
            onClick={scrollLeft}
            className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 items-center justify-center rounded-full bg-white shadow-lg hover:bg-gray-50 transition-colors"
            aria-label="Scroll left"
          >
            <ChevronLeft className="w-6 h-6 text-charcoal" />
          </button>

          {/* Right Arrow - Desktop Only */}
          <button
            onClick={scrollRight}
            className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 items-center justify-center rounded-full bg-white shadow-lg hover:bg-gray-50 transition-colors"
            aria-label="Scroll right"
          >
            <ChevronRight className="w-6 h-6 text-charcoal" />
          </button>

          <div 
            ref={scrollContainerRef}
            className="flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {testimonials.map((item, index) => (
              <div
                key={index}
                className="flex-shrink-0 w-80 md:w-96 snap-center bg-card rounded-2xl p-6 shadow-soft"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(item.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <blockquote className="font-quote text-lg italic text-foreground mb-4 leading-relaxed">
                  "{item.quote}"
                </blockquote>
                <div className="text-sm">
                  <span className="font-semibold text-foreground">{item.author}</span>
                  <span className="text-muted-foreground"> · {item.location}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// Experience Options Section
export function ExperienceOptionsSection() {
  const experiences = [
    {
      image: galleryCabanaRiverScene,
      title: "Come For The Day",
      subtitle: "Rent a Daytime Cabana Rental or In-water Covered Picnic Table",
      price: "Starting at $69",
      duration: "10am - 7pm",
      capacity: "Up to 8 people",
      description: "Your private riverside home base with shade, BBQ, seating, and access to the river all day.",
      cta: "Learn More",
      link: "/cabanas",
    },
    {
      image: coupleFloatingRiver,
      title: "Float The Guadalupe River",
      subtitle: null,
      price: "$24.99 per person",
      duration: "2-3 hour float",
      capacity: "All ages welcome",
      description: "Crystal-clear water, gentle rapids, and the float ends right at your cabana — not a parking lot.",
      cta: "Learn More",
      link: "/tubing",
    },
    {
      image: galleryGlampingTentExterior,
      title: "Stay Overnight Too in a Riverside Bell Tent",
      subtitle: null,
      price: "Starting at $99",
      duration: "7pm - 10am",
      capacity: "Up to 4 people",
      description: "AC tents with real beds, right on the riverbank. Fall asleep to the river, wake to birdsong.",
      cta: "Learn More",
      link: "/glamping",
    },
  ];

  return (
    <section className="section-padding bg-background">
      <div className="container-narrow mx-auto">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
            Choose Your Experience
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Whether you want a full day, an overnight escape, or just the float — we've got you covered.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {experiences.map((item, index) => (
            <div
              key={index}
              className="bg-card rounded-2xl overflow-hidden shadow-card hover:shadow-elevated transition-shadow duration-300 flex flex-col"
            >
              <div className="relative h-56 md:h-64 overflow-hidden">
                <img loading="lazy" decoding="async"
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="font-heading text-xl font-bold text-foreground mb-1">
                  {item.title}
                </h3>
                {item.subtitle && (
                  <p className="text-sm text-muted-foreground mb-2">
                    {item.subtitle}
                  </p>
                )}
                <div className="flex flex-col gap-1 mb-3">
                  <span className="text-primary font-bold">{item.price}</span>
                </div>
                <div className="flex gap-4 text-sm text-muted-foreground mb-4">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {item.duration}
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {item.capacity}
                  </span>
                </div>
                <p className="text-muted-foreground text-sm mb-6 flex-grow">
                  {item.description}
                </p>
                <Link to={item.link}>
                  <Button variant="outline" className="w-full">
                    {item.cta}
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Comparison Table Section
export function ComparisonSection() {
  const comparisons = [
    { feature: "Float ending", typical: "Hot parking lot", sons: "Your private cabana" },
    { feature: "Space", typical: "Fighting for tables", sons: "Reserved cabana & seating" },
    { feature: "Atmosphere", typical: "Unpredictable", sons: "Curated, family-friendly" },
    { feature: "Parking", typical: "Hunt & risk towing", sons: "Secure" },
    { feature: "Facilities", typical: "Porta-potties", sons: "Clean bathrooms & showers" },
    { feature: "Staff", typical: "Minimal", sons: "On-site management" },
  ];

  return (
    <section className="section-padding bg-muted">
      <div className="container-narrow mx-auto">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
            This Isn't Your Typical River Trip
          </h2>
          <p className="text-muted-foreground">
            See why families choose Son's Guadalupe over the alternatives
          </p>
        </div>

        <div className="bg-card rounded-2xl shadow-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-secondary/10">
                  <th className="text-left p-4 font-heading font-bold text-foreground">Feature</th>
                  <th className="text-left p-4 font-heading font-bold text-muted-foreground">Typical Trip</th>
                  <th className="text-left p-4 font-heading font-bold text-deep-green">Son's Guadalupe</th>
                </tr>
              </thead>
              <tbody>
                {comparisons.map((row, index) => (
                  <tr key={index} className="border-t border-border">
                    <td className="p-4 font-medium text-foreground">{row.feature}</td>
                    <td className="p-4 text-muted-foreground">
                      <span className="flex items-center gap-2">
                        <span className="text-destructive">✕</span>
                        {row.typical}
                      </span>
                    </td>
                    <td className="p-4 text-foreground">
                      <span className="flex items-center gap-2">
                        <span className="text-deep-green">✓</span>
                        {row.sons}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}

// Location Map Section
export function LocationMapSection() {
  const address = "7308 River Rd, New Braunfels, TX 78132";
  const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
  const embedUrl = `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3465.123!2d-98.123!3d29.789!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x865cbe8a4b4b4b4b%3A0x4b4b4b4b4b4b4b4b!2s7308%20River%20Rd%2C%20New%20Braunfels%2C%20TX%2078132!5e0!3m2!1sen!2sus!4v1234567890`;

  return (
    <section className="section-padding bg-cream">
      <div className="container-narrow mx-auto">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
            Find Us on the Guadalupe
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Located on River Road in New Braunfels, just minutes from the heart of Texas Hill Country
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-center">
          {/* Map */}
          <div className="relative h-80 md:h-96 rounded-2xl overflow-hidden shadow-elevated">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3465.5!2d-98.13!3d29.79!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x865cbd8c8b8b8b8b%3A0x8b8b8b8b8b8b8b8b!2s7308%20River%20Rd%2C%20New%20Braunfels%2C%20TX%2078132!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Son's Guadalupe Location"
              className="absolute inset-0"
            />
          </div>

          {/* Info Card */}
          <div className="bg-white rounded-2xl p-8 shadow-soft">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-teal/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <MapPin className="w-6 h-6 text-teal" />
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold text-charcoal mb-1">
                  Son's Guadalupe
                </h3>
                <p className="text-charcoal/70">
                  7308 River Rd<br />
                  New Braunfels, TX 78132
                </p>
              </div>
            </div>

            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-3 text-charcoal/70">
                <Clock className="w-5 h-5 text-teal" />
                <span>Open 9 AM - 6 PM Daily</span>
              </div>
              <div className="flex items-center gap-3 text-charcoal/70">
                <Car className="w-5 h-5 text-teal" />
                <span>7 miles from Loop 337</span>
              </div>
              <div className="flex items-center gap-3 text-charcoal/70">
                <Navigation className="w-5 h-5 text-teal" />
                <span>½ mile past second crossing on the right</span>
              </div>
            </div>

            <a
              href={googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <Button variant="default" size="lg" className="w-full gap-2">
                <Navigation className="w-5 h-5" />
                Get Directions
              </Button>
            </a>
            
            <p className="text-center text-charcoal/60 text-sm mt-4">
              Near the Lone Star Float House
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// Final CTA Section
export function FinalCTASection() {
  return (
    <section className="section-padding bg-deep-green text-primary-foreground">
      <div className="container-narrow mx-auto text-center">
        <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
          Ready for a Fun & Relaxing Guadalupe Getaway?
        </h2>
        <p className="text-primary-foreground/80 text-lg mb-8 max-w-2xl mx-auto">
          Summer weekends fill up fast — only 21 Cabanas available.
          <br />
          Secure your spot today!
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/book-now">
            <Button variant="hero" size="xl">
              Check Availability & Book
            </Button>
          </Link>
          <a href="tel:877-577-7667">
            <Button variant="hero-outline" size="xl">
              Call 877-577-7667
            </Button>
          </a>
        </div>
        <p className="text-primary-foreground/60 text-sm mt-8">
          Questions? Our team is available 9am-7pm daily to help you plan the perfect day.
        </p>
      </div>
    </section>
  );
}